import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BuyCreditsModal } from "@/components/dashboard/CreditsCard";

// One-per-session low-credit nudge. Fires when a paid Kickstart / Starter
// / Basis / Plus user is at ≤10 credits remaining. Skipped for trial,
// founder / demo / unlimited / billing-exempt users, and once dismissed
// it stays dismissed for the rest of the browser session.
const SESSION_KEY = "zy-low-credit-nudge-shown";
const THRESHOLD = 10;

const KICKSTART_CAP = {
  "Kickstart 1": 50,
  "Kickstart 2": 150,
  "Kickstart 3": 300,
};

export default function LowCreditsNudge() {
  const { user } = useAuth();
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const [visible, setVisible] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);

  useEffect(() => {
    setVisible(false);
    if (!ownWorkspace || !user) { setBuyOpen(false); return; }
    // Fix (2026-08-30): the only accounts that see a credit meter /
    // buy / low-credit nudge are Kickstart licence holders. Everyone
    // else (subscription tiers, trial, founder, unlimited) has no
    // credit surface at all. This supersedes the earlier "any paid
    // signal" gate.
    const plan = String(user.subscription_plan || user.tier?.plan_key || "");
    if (!plan.startsWith("Kickstart")) return;
    if (user.is_founder || user.is_demo || user.is_unlimited) return;

    // Kickstart is capped — enforce a numeric remaining even if the
    // backend still returns Unlimited for a legacy account.
    const cap = KICKSTART_CAP[plan] || null;
    const used = Number.isSafeInteger(user.tier?.ai_credits_used) && user.tier.ai_credits_used >= 0
      ? user.tier.ai_credits_used : 0;
    const backendLimit = user.tier?.ai_credits_limit;
    const limit = Number.isSafeInteger(backendLimit) && backendLimit >= (cap || 0)
      ? backendLimit : cap;
    const serverRemaining = user.tier?.ai_credits_remaining;
    const remaining = Number.isSafeInteger(serverRemaining) && serverRemaining >= 0
      ? serverRemaining : limit == null ? undefined : Math.max(0, limit - used);

    if (limit === null || limit === undefined) return; // truly unlimited plan
    if (remaining === null || remaining === undefined) return;
    if (remaining > THRESHOLD) return;

    // Once per browser session.
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch { /* sessionStorage may be blocked */ }

    setVisible(true);
  }, [user, ownWorkspace]);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const openBuy = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
    setBuyOpen(true);
  };

  if (!ownWorkspace || (!visible && !buyOpen)) return null;

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="low-credit-nudge"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <span
                className="inline-flex items-center justify-center rounded-lg"
                style={{ background: "#FFF7E0", color: "#D4AF37", width: 40, height: 40 }}
              >
                <Sparkles size={18} />
              </span>
              <button
                type="button"
                onClick={dismiss}
                data-testid="low-credit-nudge-close"
                className="p-1.5 rounded hover:bg-[#f5f5f5] text-[#666]"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <h2 className="text-[19px] font-bold text-[#0A1628]">Low credits</h2>
            <p className="text-[13.5px] text-[#555] mt-1.5">
              You have <strong>{Math.max(0, Number(user?.tier?.ai_credits_remaining ?? 0))}</strong>{" "}
              AI credit{(user?.tier?.ai_credits_remaining ?? 0) === 1 ? "" : "s"} left this period.
              Top up now to keep your assistants running without interruption.
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={dismiss}
                data-testid="low-credit-nudge-dismiss"
                className="rounded-full border border-[#0A162820] px-4 py-2 text-[13px] font-semibold text-[#0A1628] hover:bg-[#F7F8FA]"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={openBuy}
                data-testid="low-credit-nudge-buy"
                className="rounded-full bg-[#202B60] text-white px-4 py-2 text-[13px] font-semibold hover:opacity-95"
              >
                Buy credits
              </button>
            </div>
          </div>
        </div>
      )}
      {buyOpen && <BuyCreditsModal onClose={() => setBuyOpen(false)} />}
    </>
  );
}
